'use client'

import { useLocale, useTranslations } from "next-intl"
import styles from "./page.module.css"
import { IconButton, InputAdornment, TextField } from "@mui/material"
import { FormEvent, useEffect, useRef, useState } from "react"
import { getFieldError, toBase64, validateForm } from "@/utils/helpers"
import Button, { ButtonSize } from "@/components/UI/Buttons/Button/Button"
import AppLink from "@/components/UI/Links/AppLink/AppLink"
import { isEmail, isRequired, mixedLetters, numberOfCharacters, numbersAndSpecialCharacters } from "@/utils/validators"
import IconShowPassword from '@/assets/img/Icon_Show-Password.svg'
import IconHidePassword from '@/assets/img/Icon_Hide-Password.svg'
import { FieldState, ValidationsForForm } from "@/interfaces/api/forms/forms_types"
import { API_Error, API_Success_Response } from "@/interfaces/api/response/api_response_types"
import TooltipField from "@/components/UI/Tooltips/TooltipField/TooltipField"
import Notification from "@/components/Notification/Notification"
import { toast } from "react-toastify"
import CustomCheckbox from "@/components/UI/Buttons/Checkbox/CustomCheckbox"
import Icon from "@/components/UI/Icon/Icon"
import IconInfo from "@/assets/img/Icon_Info.svg"
import { useSignUp } from "@/apiUtilities/mutations/useSignUp"
import { SignUpData, SignUpErrorsSubtypes } from "@/interfaces/api/signup/api_request_types"
import { useAuthStore } from "@/stores/useAuthStore"
import { API_Data_SignUp_Response } from "@/interfaces/api/signup/api_response_types"
import { useModalStore } from "@/stores/useModalStore"
import UserAlreadyExistsModal from "@/components/Modals/UserAlreadyExistsModal/UserAlreadyExistsModal"
import { useCompleteAccountStore } from "@/stores/useCompleteAccountStore"
import { useRouter } from "@/i18n/navigation"
import { ProfileRoles } from "@/interfaces/profile_types"
import { CompleteAccountData } from "@/stores/useCompleteAccountStore"
import { FEATURES } from "@/utils/constants"
import ExtraLabel from "@/components/UI/Label/ExtraLabel"
import { useWindowSize } from "@/hooks/useWindowsSize"
import { BREAKPOINTS } from "@/utils/constants"

interface SignUpFormState {
  username: FieldState<string>
  password: FieldState<string>
}

/**
 * Initial (empty) validations object for the signup form.
 */
const initialValidations: ValidationsForForm<SignUpFormState> = {
  username: [],
  password: []
}

const defaultProfileName = 'User'
const defaultBirthday = '1970-01-01T00:00:00Z'

export default function SignUp() {
  const locale = useLocale()
  const t = useTranslations('SignUp')
  const te = useTranslations('Errors')
  const router = useRouter()
  const signUp = useSignUp()
  const { setCredentials } = useAuthStore()
  const { openModal, closeModal } = useModalStore()
  const { width } = useWindowSize()
  const formRef = useRef<HTMLFormElement | null>(null)
  const userNameRef = useRef<HTMLInputElement | null>(null)
  const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [passwordFocused, setPasswordFocused] = useState<boolean>(false)
  const { setData, clearData } = useCompleteAccountStore()
  const [formState, setFormState] = useState<SignUpFormState>({
    username: {
      value: '',
      dirty: false,
      required: true,
      validators: [
        { validator: isRequired, show: false },
        { validator: isEmail, show: false }
      ]
    },
    password: {
      value: '',
      dirty: false,
      required: true,
      validators: [
        { validator: isRequired,  show: false },
        { validator: mixedLetters, show: true },
        { validator: numbersAndSpecialCharacters, show: true },
        { validator: (v:string) => numberOfCharacters(v, 8, 32), show: true}
      ]
    }
  })
  const [validations, setValidations] = useState<ValidationsForForm<SignUpFormState>>(initialValidations)
  const [showServerError, setShowServerError] = useState<string>('')
  const [usernameFocused, setUsernameFocused] = useState<boolean>(false)
  const [legals, setLegals] = useState(false)
  const [discounts, setDiscounts] = useState(false)

  /**
   * Handles input changes for both username and password fields.
   * Marks the field as `dirty` and updates its `value`.
   * @param e Input change event from the TextField.
   */
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const key = e.target.name as keyof SignUpFormState
    if (!!showServerError) setShowServerError('')
    setFormState(formStatePrev => {
      return {
        ...formStatePrev,
        [key]: {
          ...formStatePrev[key],
          value: e.target.value,
          dirty: e.target.value !== ''
        }
      }
    })
  }

  /**
   * Handles sign up API errors and updates UI state accordingly.
   *
   * @param response - The API error response returned by the backend.
   */
  async function handleErrorSignup (response: API_Error) {
    if(response.code === 400 && (!!response.errors && response.errors[0].errno === SignUpErrorsSubtypes.USER_ALREADY_EXISTS)) {
      openModal({
        content: <UserAlreadyExistsModal onClose={closeModal} />
      })
      return
    }

    toast.error(<Notification text={te('defaultUnknowError')} />)
  }

  async function handleSuccessSignup (response: API_Success_Response<API_Data_SignUp_Response, CompleteAccountData>) {
    setCredentials(formState.username.value, formState.password.value, response.data!.user.id)
    toast.success(<Notification text={t('signUpSuccess')} />)
    if(response.error && response.error.data) {
      setData(response.error.data)
      router.push("/complete-account?registration=true")
    } else {
      router.push("/billing/choose-plan?registration=true")
    }
  }

  /**
   * Handles form submission:
   * - prevents the default browser submit
   * - creates the Basic Auth header value (base64 "username:password")
   * - triggers the signup mutation
   * @param e Form submit event.
   */
  function handleSubmit(e:FormEvent) {
    e.preventDefault()

    const basicAuth = toBase64(formState.username.value + ":" + formState.password.value)
    const body: SignUpData = {
      data: {
        user: {
          username: formState.username.value,
          password: formState.password.value,
          ctry_code: '',
          preferred_lang: locale,
          accepted_terms: Number(legals),
          accepted_news: Number(discounts)
        },
        profile: {
          name: defaultProfileName,
          role: ProfileRoles.SUPER,
          birthday: defaultBirthday,
          avatar: {
            avatar_id: ''
          }
        }
      }
    }

    signUp.mutate({ basicAuth, payload: body},
      {
        onSuccess: (response) => handleSuccessSignup(response),
        onError: (response) => handleErrorSignup(response)
      }
    )
  }

  /**
   * Focuses the username field when the component mounts and the screen width is at least the desktop breakpoint.
   */
  useEffect(() => {
    if (width && width >= BREAKPOINTS.DESKTOP) {
      userNameRef.current?.focus()
    }
  }, [width])

  /**
   * Re-validates the form whenever a field value changes.
   * Updates both:
   * - `validations` (to show messages in the tooltips)
   * - `formIsValid` (to enable/disable the submit button)
   */
  useEffect(() => {
    const { validations: preValidations, isValid } = validateForm(formState, initialValidations)
    setValidations(preValidations)
    setFormIsValid(isValid)
  }, [formState])

  /**
   * Focuses the username field if there's a server error (e.g., wrong credentials) to prompt the user to correct it.
   */
  useEffect(() => {
    if (!!showServerError && userNameRef.current) userNameRef.current.focus()
  }, [showServerError])

  const userNameError = getFieldError<SignUpFormState>(formState, validations, 'username')
  const passwordError = getFieldError<SignUpFormState>(formState, validations, 'password')

  return (
    process.env.NEXT_PUBLIC_FEATURE_LOGIN === "true" &&
      <form ref={formRef} className={styles.form} onSubmit={handleSubmit} data-testid="SignUp">
        <div className={styles.signUpWrapper}>
          <div className={styles.contentWrapper}>
            <div className={styles.titleWrapper}>
              <h1 className={`title2 ${styles.title}`} data-testid="SignUp_title">{t('title')}</h1>
              <ExtraLabel content={t('titleLabel')} />
            </div>

            <div className={styles.fields}>
              <TooltipField
                validations={validations.username}
                placement="bottom-start"
                arrow={false}
                disableHoverListener={true}
                disableTouchListener={true}
                hasHelperText={true}
                open={usernameFocused}
              >
                <TextField
                  data-testid="SignUp_username"
                  type="text"
                  name="username"
                  label={t('usernameLabel')}
                  value={formState.username.value}
                  error={!!showServerError || (userNameError.hasError && !usernameFocused)}
                  helperText={!usernameFocused && userNameError.msg !== undefined ? t(`errors.fields.${userNameError.msg}`) : ' '}
                  onChange={handleChange}
                  inputRef={userNameRef}
                  onFocus={() => {
                    setUsernameFocused(true)
                    if (width && width < BREAKPOINTS.DESKTOP) {
                      formRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                      })
                    }
                  }}
                  onBlur={() => setUsernameFocused(false)}
                />
              </TooltipField>

              <TooltipField
                validations={validations.password}
                placement="bottom-start"
                arrow={false}
                disableHoverListener={true}
                disableTouchListener={true}
                hasHelperText={true}
                open={passwordFocused}
              >
                <TextField
                  data-testid="SignUp_password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label={t('passwordLabel')}
                  value={formState.password.value}
                  error={(passwordError.hasError || !!showServerError) && !passwordFocused}
                  helperText={!!showServerError ? t(`errors.server.${showServerError}`) : !passwordFocused && passwordError.msg !== undefined ? t(`errors.fields.${passwordError.msg}`) : ' '}
                  onChange={handleChange}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  slotProps={{
                    input: {
                      endAdornment:
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(prev => !prev)}
                          edge="end"
                        >
                          {showPassword
                            ? <IconHidePassword className={styles.passwordIcon} />
                            : <IconShowPassword className={styles.passwordIcon} />
                          }
                        </IconButton>
                      </InputAdornment>
                    }
                  }}
                />
              </TooltipField>

              <div className={styles.checkboxWrapper}>
                <CustomCheckbox
                  data-testid="Signup_legals"
                  id='legals'
                  onChange={() => setLegals(prev => !prev)}
                  checked={legals}
                  />
                <p>{t.rich('legals', {
                terms: (chunks) => <AppLink href="/eula">{chunks}</AppLink>,
                privacy: (chunks) => <AppLink href="/privacy">{chunks}</AppLink>,
                required: (chunks) => <span className={`bodyTextSmall ${styles.requiredText}`}>{chunks}</span>
                })}</p>
              </div>

              {FEATURES.newsletter &&
              <div className={styles.checkboxWrapper}>
                <CustomCheckbox
                  data-testid="Signup_discounts"
                  id="discounts"
                  onChange={() => setDiscounts(prev => !prev)}
                  checked={discounts}
                />
                <p>{t('discounts')}</p>
              </div>
              }
            </div>
          </div>

          <div className={styles.disclaimer} data-testid="SignUp_disclaimer">
            <Icon icon={IconInfo} className={styles.info} />
            <p className="bodyTextSmall">{t('disclaimer', { siteName: process.env.NEXT_PUBLIC_SITENAME as string })}</p>
          </div>

          <div className={styles.btnWrapper}>
            <Button data-testid="Signup_submit" className={styles.nextBtn} type={"submit"} size={ButtonSize.LARGE} disabled={!formIsValid || !!showServerError || signUp.isPending || !legals}>{t('nextButton')}</Button>
          </div>

          <div className={styles.loginDisclaimer}>
            <p className={'bodyTextMediumStrong'}>{t('haveAccount')}</p>
            <AppLink data-testid="Signup_login" href="/login" className={styles.loginLink} highlight={true}>{t('login')}</AppLink>
          </div>
        </div>
      </form>
  )
}